<template>
  <div>
    <default-modal
      :title="'Send job post to matched job seeker'"
      :confirmLabel="'Send'"
      @onConfirm="handleModalConfirm"
      @onCancel="handleModalCancel"
      :showCancel="jobsList.length > 0"
      :showActions="jobsList.length > 0"
      id="send-jobs-modal"
      :disableConfirmButton="jobsList.length ? !selectedJobs.length : false"
      :description="
        jobsList.length
          ? `Please select one or more existing job posts to send to this matched job seeker.`
          : `You have no active job postings. Please create a job posting.`
      "
    >
      <!-- ✅ If jobs exist → show checkboxes -->
      <template v-slot:input>
        <template v-if="jobsList.length && !justCreatedJob">
          <div
            class="jobs-list my-2 ml-4"
            v-for="job in jobsList"
            :key="job._id"
          >
            <label :class="job.alreadySent ? 'disabled' : ''">
              <input
                type="checkbox"
                :value="job._id"
                v-model="selectedJobs"
                :disabled="job.alreadySent"
              />
              <span class="checkmark"></span>
              {{ job.name }} {{ job.alreadySent ? "(Already Sent)" : "" }}
            </label>
          </div>
        </template>

        <!-- ✅ No jobs? Show Job Create Form -->
        <template v-else>
          <job-form
            ref="jobForm"
            :loading="jobFormLoading"
            :currentView="'edit'"
            :job="null"
            :isCopyMode="false"
            :hideCancelButton="true"
            @set-loading="setJobFormLoading"
            @job-saved="handleNewJobCreated"
            @close-modal="closeModal"
            :labelText="'Send'"
            :cancelButtonTo="'/employees/company-matches'"
            :isSendingJobPost="true"
          />
        </template>
      </template>
    </default-modal>
  </div>
</template>

<script>
import { nanoid } from "nanoid"
import messagesMixin from "@/mixins/messagesMixin"
import paymentsMixin from "@/mixins/paymentsMixin"
import stripeMixin from "@/mixins/stripeMixin"
import { STRIPE_PRODUCTS } from "@/utils/constants"
import JobForm from "@/components/Job/JobForm"
import Jobs from "@/api/jobs"

export default {
  name: "send-jobs-modal",
  props: {
    selectedJobseeker: {
      type: String,
      required: true,
    },
  },
  mixins: [messagesMixin, paymentsMixin, stripeMixin],
  components: { JobForm },
  data() {
    const paymentIntent = this.$route.query?.payment_intent
    return {
      selectedJobs: [],
      sentJobPosts: [],
      activeJobs: [],
      product: null,
      productSlug: STRIPE_PRODUCTS.SEND_JOB_POST_TO_MATCH,
      loading: false,
      jobFormLoading: false,
      paymentIntent,
      justCreatedJob: false,
    }
  },
  methods: {
    closeModal() {
      const modal = document.getElementById("send-jobs-modal")
      if (modal) {
        const closeBtn = modal.querySelector(".close")
        if (closeBtn) closeBtn.click()
      }
    },
    async handleSendJobsPost() {
      const paymentIntentData = {
        job_ids: this.selectedJobs,
        candidate_profile_id: this.selectedJobseeker,
        product_slug: STRIPE_PRODUCTS.SEND_JOB_POST_TO_MATCH,
        return_url: this.returnUrlPath,
        idempotencyKey: nanoid(),
      }

      const response = await this.$store.dispatch(
        "api/createPaymentIntent",
        paymentIntentData
      )

      const defaultErrorMessage = "We couldn't submit your payment request."
      if (!response.data || response.data.errors) {
        throw new Error(response.data?.errors ?? defaultErrorMessage)
      }
      const paymentIntent = response.data

      if (paymentIntent) {
        if (paymentIntent.free) {
          // Product price is $0 — the backend already sent the invitation,
          // so skip the payment_intent/checkout flow entirely. Delay the
          // reload so the toast is visible before the page unloads.
          this.$toast.success("Your email has been sent")
          await this.$router.push(this.returnUrlPath)
          setTimeout(() => window.location.reload(), 2500)
          return
        }
        const urlPath =
          this.returnUrlPath === "/employees/company-matches"
            ? `${this.returnUrlPath}?payment_intent=${paymentIntent.id}&profileId=${this.selectedJobseeker}`
            : `${this.returnUrlPath}&payment_intent=${paymentIntent.id}&profileId=${this.selectedJobseeker}`
        await this.$router.push(urlPath)
        window.location.reload()
      } else {
        throw new Error(defaultErrorMessage)
      }
    },
    handleModalCancel() {
      this.selectedJobs = []
      this.justCreatedJob = false
    },
    handleModalHide() {
      window.location.reload()
    },
    async handleModalConfirm() {
      try {
        if (this.jobsList.length) {
          this.handleSendJobsPost()
        } else {
          await this.$refs.jobForm?.submit?.()
        }
      } catch (error) {
        console.log("error", error)
        this.logDevError(error)
      }
    },
    setLoading(loading) {
      this.loading = loading
    },
    setDataRetrieving(isRetrieving) {
      this.dataRetrieving = isRetrieving
    },
    async getJobs() {
      const { data } = await Jobs.getJobs({ activeOnly: true })
      this.activeJobs = data.filter((job) => !job.briefing)
    },
    async getProfileAccessDetails() {
      const userId = this.$store.state.user?._id
      const { data } = await this.$store.dispatch("api/getCompanyUserProfile", {
        userId,
      })
      await this.$store.commit("UPDATE_USER_PROFILE", data)

      this.sentJobPosts = data?.provider.sentJobPosts ?? []
      this.unlockedApplicants = data?.unlockedApplicants ?? []
      this.showLoading = false
    },
    setJobFormLoading(val) {
      this.jobFormLoading = val
    },

    async handleNewJobCreated(newJob) {
      const jobId = newJob._id || newJob.id
      this.selectedJobs = [jobId]
      this.justCreatedJob = true
      await this.getJobs()
      await this.$nextTick()
      await this.handleSendJobsPost()
    },
  },
  computed: {
    jobsList() {
      let allJobs = this.activeJobs

      if (!this.selectedJobseeker) {
        return allJobs.map((job) => ({ ...job, alreadySent: false }))
      }

      const sentJobIds = new Set(
        this.sentJobPosts
          .filter((post) => post.profile == this.selectedJobseeker)
          .map((post) => post.job)
      )

      allJobs = allJobs.map((job) => ({
        ...job,
        alreadySent: sentJobIds.has(job._id),
      }))

      return allJobs
    },
    returnUrlPath() {
      const { query, path } = this.$route
      const filteredQuery = { ...query }
      delete filteredQuery.payment_intent

      const newPath = this.$router.resolve({ path, query: filteredQuery }).href

      return newPath
    },
  },
  mounted() {
    setTimeout(() => {
      this.getJobs()
      this.getProfileAccessDetails()
    }, 3000)
  },
  async created() {
    console.log("jobseeker", this.selectedJobseeker)
    const product = await this.getProduct(this.productSlug)
    this.product = product
    this.dataRetrieving = true
  },
}
</script>

<style lang="scss" scoped>
.modal {
  & .modal-header {
    & .close span {
      font-size: 2.5rem;
    }
  }
}
</style>
